import { useState } from 'react';
import { api, ApiError } from '../api.ts';

/**
 * Registering a care home - RX-ORG-01.
 *
 * Until this existed there was a `POST /api/orgs` endpoint and no way to
 * reach it, so a care home arriving from the "For organisations" button on
 * the marketing site was told it belonged to no organisation and given no way
 * forward. A signup funnel that ends in a dead end is worse than no funnel:
 * they came because they were interested.
 *
 * The organisation is created `pending` and can hold no resident record until
 * somebody at DrBell approves it. That is stated here rather than discovered
 * later, because "I signed up and nothing works" is the support ticket this
 * paragraph prevents.
 */
const TYPES = [
  { id: 'care_home', label: 'Care home' },
  { id: 'hospital', label: 'Hospital' },
  { id: 'clinic', label: 'Clinic' },
  { id: 'home_care', label: 'Home-care team' },
  { id: 'pharmacy', label: 'Pharmacy' },
  { id: 'other', label: 'Something else' },
] as const;

/** A slug the server will accept, derived from what they typed. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export function Register({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [type, setType] = useState<string>('care_home');
  const [contactEmail, setContactEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const slugOk = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(effectiveSlug) && effectiveSlug.length >= 2;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/orgs', {
        name: name.trim(),
        slug: effectiveSlug,
        type,
        ...(contactEmail.trim() ? { contactEmail: contactEmail.trim() } : {}),
      });
      setDone(true);
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 409
          ? 'That short name is already taken. Try another.'
          : e instanceof ApiError
            ? e.message
            : String(e),
      );
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto mt-24 w-full max-w-md rounded-xl border border-(--line) bg-(--panel) p-6">
        <h1 className="mb-2 text-xl font-semibold">{name} is registered</h1>
        <p className="mb-4 text-sm text-(--muted)">
          It is <strong className="text-(--fg)">awaiting approval</strong>. Until
          somebody at DrBell approves it, the organisation cannot hold a
          resident record — that check exists because "we are a care home" is a
          claim about the real world that software cannot verify.
        </p>
        <p className="mb-5 text-sm text-(--muted)">
          We aim to approve within one working day. If it is urgent, write to{' '}
          <a className="text-(--accent)" href="mailto:hello@drbell.app">
            hello@drbell.app
          </a>
          .
        </p>
        <button
          onClick={onCreated}
          className="w-full rounded-lg bg-(--accent) py-2 text-sm font-medium text-white"
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-16 w-full max-w-md rounded-xl border border-(--line) bg-(--panel) p-6">
      <h1 className="mb-1 text-xl font-semibold">Register your organisation</h1>
      <p className="mb-5 text-sm text-(--muted)">
        You will be its owner. Nothing is charged today.
      </p>

      <label className="mb-3 block">
        <span className="text-xs font-medium text-(--muted)">Name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Sunrise Care Home"
          className="mt-1 w-full rounded-lg border border-(--line) bg-(--bg) px-3 py-2 outline-none focus:border-(--accent)"
        />
      </label>

      <label className="mb-3 block">
        <span className="text-xs font-medium text-(--muted)">
          Short name — appears in links
        </span>
        <input
          value={effectiveSlug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value.toLowerCase());
          }}
          placeholder="sunrise-care"
          spellCheck={false}
          className="mt-1 w-full rounded-lg border border-(--line) bg-(--bg) px-3 py-2 font-mono text-sm outline-none focus:border-(--accent)"
        />
        {/* Derived from the name until they edit it, then left alone. A field
            that keeps overwriting what somebody typed is maddening. */}
        {effectiveSlug !== '' && !slugOk && (
          <span className="mt-1 block text-xs text-amber-500">
            Lowercase letters, digits and hyphens only.
          </span>
        )}
      </label>

      <label className="mb-3 block">
        <span className="text-xs font-medium text-(--muted)">Type</span>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="mt-1 w-full rounded-lg border border-(--line) bg-(--bg) px-3 py-2 outline-none focus:border-(--accent)"
        >
          {TYPES.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </label>

      <label className="mb-4 block">
        <span className="text-xs font-medium text-(--muted)">
          Contact email — optional, for approval
        </span>
        <input
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          type="email"
          autoComplete="email"
          placeholder="manager@sunrisecare.in"
          className="mt-1 w-full rounded-lg border border-(--line) bg-(--bg) px-3 py-2 outline-none focus:border-(--accent)"
        />
      </label>

      {error && (
        <p role="alert" className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
          {error}
        </p>
      )}

      <button
        disabled={busy || name.trim().length < 2 || !slugOk}
        onClick={() => void submit()}
        className="w-full rounded-lg bg-(--accent) py-2 font-medium text-white disabled:opacity-40"
      >
        {busy ? 'Registering…' : 'Register'}
      </button>

      <p className="mt-3 text-xs text-(--muted)">
        By registering you agree to the{' '}
        <a className="text-(--accent)" href="https://drbell.app/legal/terms">terms</a> and{' '}
        <a className="text-(--accent)" href="https://drbell.app/legal/privacy">privacy policy</a>.
      </p>
    </div>
  );
}
